/**
 * Bookmarks Management System
 * Handles adding, removing, and managing bookmarked pages in localStorage
 */

const BOOKMARKS_STORAGE_KEY = 'besper_bookmarks';

/**
 * Get all bookmarks from localStorage
 * @returns {Array} Array of bookmarked page objects
 */
export const getBookmarks = () => {
  try {
    const bookmarks = localStorage.getItem(BOOKMARKS_STORAGE_KEY);
    return bookmarks ? JSON.parse(bookmarks) : [];
  } catch (error) {
    console.error('Error reading bookmarks from localStorage:', error);
    return [];
  }
};

/**
 * Save bookmarks to localStorage
 * @param {Array} bookmarks - Array of bookmarked page objects
 */
export const saveBookmarks = (bookmarks) => {
  try {
    localStorage.setItem(BOOKMARKS_STORAGE_KEY, JSON.stringify(bookmarks));
  } catch (error) {
    console.error('Error saving bookmarks to localStorage:', error);
  }
};

/**
 * Add a page to bookmarks
 * @param {Object} page - Page object with path, label, and optional icon
 * @returns {boolean} Success status
 */
export const addToBookmarks = (page) => {
  try {
    const bookmarks = getBookmarks();
    
    // Check if already exists
    const exists = bookmarks.some(bookmark => bookmark.path === page.path);
    if (exists) {
      return false; // Already exists
    }
    
    // Add timestamp for ordering
    const bookmarkedPage = {
      ...page,
      addedAt: new Date().toISOString(),
      id: `${page.path}_${Date.now()}` // Unique ID
    };
    
    bookmarks.push(bookmarkedPage);
    saveBookmarks(bookmarks);
    
    // Dispatch custom event for immediate UI updates
    window.dispatchEvent(new CustomEvent('bookmarkChanged', {
      detail: { action: 'add', path: page.path }
    }));
    
    return true;
  } catch (error) {
    console.error('Error adding to bookmarks:', error);
    return false;
  }
};

/**
 * Remove a page from bookmarks
 * @param {string} path - Path of the page to remove
 * @returns {boolean} Success status
 */
export const removeFromBookmarks = (path) => {
  try {
    const bookmarks = getBookmarks();
    const filteredBookmarks = bookmarks.filter(bookmark => bookmark.path !== path);
    
    if (filteredBookmarks.length === bookmarks.length) {
      return false; // Not found
    }
    
    saveBookmarks(filteredBookmarks);
    
    // Dispatch custom event for immediate UI updates
    window.dispatchEvent(new CustomEvent('bookmarkChanged', {
      detail: { action: 'remove', path: path }
    }));
    
    return true;
  } catch (error) {
    console.error('Error removing from bookmarks:', error);
    return false;
  }
};

/**
 * Check if a page is bookmarked
 * @param {string} path - Path of the page to check
 * @returns {boolean} Whether the page is bookmarked
 */
export const isBookmarked = (path) => {
  try {
    const bookmarks = getBookmarks();
    return bookmarks.some(bookmark => bookmark.path === path);
  } catch (error) {
    console.error('Error checking bookmarks:', error);
    return false;
  }
};

/**
 * Toggle bookmark status of a page
 * @param {Object} page - Page object
 * @returns {boolean} New bookmark status (true if added, false if removed)
 */
export const toggleBookmark = (page) => {
  if (isBookmarked(page.path)) {
    removeFromBookmarks(page.path);
    return false;
  } else {
    addToBookmarks(page);
    return true;
  }
};

/**
 * Get bookmarks sorted by most recently added
 * @returns {Array} Sorted array of bookmarks
 */
export const getSortedBookmarks = () => {
  const bookmarks = getBookmarks();
  return bookmarks.sort((a, b) => new Date(b.addedAt) - new Date(a.addedAt));
};

/**
 * Clear all bookmarks
 */
export const clearAllBookmarks = () => {
  try {
    localStorage.removeItem(BOOKMARKS_STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing bookmarks:', error);
  }
};

/**
 * Get page info for bookmarks based on current route
 * @param {string} pathname - Current pathname
 * @param {string} search - Current search params
 * @returns {Object} Page info object
 */
export const getPageInfoForBookmarks = (pathname, search = '') => {
  // Remove language prefix for consistent storage
  const cleanPath = pathname.replace(/^\/[a-z]{2}(?=\/)/, '') || '/';
  const fullPath = cleanPath + search;

  const q = search.startsWith('?') ? search.slice(1) : search;
  if (new URLSearchParams(q).get('tab') === 'resources' && (pathname === '/' || /^\/[a-z]{2}$/.test(pathname))) {
    return {
      path: pathname + search,
      label: 'Resources',
      cleanPath: pathname === '/' ? '/' : pathname,
    };
  }

  // Map common routes to user-friendly labels
  const routeLabels = {
    '/': 'Home',
    '/dashboard': 'Dashboard',
    '/tenants': 'Tenants',
    '/users': 'User Management',
    '/resources': 'Resources',
    '/cost-pools': 'Cost Pool Management',
    '/profile': 'User Profile',
    '/about': 'About Us',
    '/docs': 'Documentation',
    '/help-center': 'Help Center',
    '/faq': 'FAQ',
    '/contact': 'Contact',
    '/terms': 'Terms of Service',
    '/privacy': 'Privacy Policy',
    '/blog': 'Blog',
    '/changelog': 'Changelog',
    '/community': 'Community',
    '/feedback': 'Feedback',
    '/partners': 'Partners',
    '/become-partner': 'Become Partner',
    '/press': 'Press',
    '/investors': 'Investors',
    '/newsletter': 'Newsletter',
    '/gdpr': 'GDPR',
    '/cookie-settings': 'Cookie Settings',
    '/impressum': 'Impressum',
    '/sla': 'Service Level Agreement'
  };
  
  // Generate label from path
  let label = routeLabels[cleanPath];
  if (!label) {
    // For dynamic routes, create a meaningful label
    if (cleanPath.includes('/tenant/') && cleanPath.includes('/resource/')) {
      const pathParts = cleanPath.split('/');
      const tenantId = pathParts[2];
      const resourceId = pathParts[4];
      label = `Resource ${resourceId} (${tenantId})`;
    } else {
      // Fallback: capitalize and clean the path
      label = cleanPath
        .replace(/^\//, '')
        .replace(/-/g, ' ')
        .replace(/\//g, ' / ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
  }
  
  return {
    path: fullPath,
    label: label,
    cleanPath: cleanPath
  };
};